import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {AgentIcon, DeviceDesktopIcon, FileDirectoryIcon, PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {ConnectDeviceDialog} from './ConnectDeviceDialog'
import {useConnectedDevices, type ConnectedDevice} from '../../hooks/useConnectedDevices'
import styles from './EnvironmentPicker.module.css'

export type EnvironmentKind = 'cloud' | 'local'

export type EnvironmentOption = {
  id: string
  kind: EnvironmentKind
  label: string
  icon: React.ComponentType<{size?: number | 'small' | 'medium' | 'large'}>
  /** For `local` device options: the absolute working-directory path
   * the chosen child session will spawn in. */
  workingDirectory?: string
  /** For `local` device options: the device this option points at,
   * used to look up directories for the secondary picker. */
  deviceId?: string
}

const DEFAULT_ENVIRONMENTS: EnvironmentOption[] = [
  {id: 'background-agent', kind: 'cloud', label: 'Background agent', icon: AgentIcon},
]

const DEVICE_OPTION_PREFIX = 'local-device:'

const deviceOptionId = (deviceId: string, path: string) => `${DEVICE_OPTION_PREFIX}${deviceId}::${path}`

const buildLocalOptions = (devices: ConnectedDevice[]): EnvironmentOption[] =>
  devices.flatMap(d =>
    d.directories.map(dir => ({
      id: deviceOptionId(d.id, dir.path),
      kind: 'local' as const,
      label: d.displayName,
      icon: DeviceDesktopIcon,
      workingDirectory: dir.path,
      deviceId: d.id,
    })),
  )

type EnvironmentPickerProps = {
  /** Available environments to choose from. Grouped in the dropdown by `kind`,
   * and (for `local`) further grouped by `deviceId`. If omitted, defaults to
   * the built-in cloud environments plus the live list of connected local
   * devices fetched via `useConnectedDevices` (one option per device working
   * directory). */
  environments?: EnvironmentOption[]
  selectedEnvironmentId?: string
  onSelectEnvironment?: (environment: EnvironmentOption) => void
  /** Called when the currently selected environment is no longer in the available
   * list (e.g. a local device disconnected between selection and submit). */
  onSelectedEnvironmentUnavailable?: () => void
  onConnectDevice?: () => void
  disabled?: boolean
}

export function EnvironmentPicker({
  environments,
  selectedEnvironmentId,
  onSelectEnvironment,
  onSelectedEnvironmentUnavailable,
  onConnectDevice,
  disabled = false,
}: EnvironmentPickerProps) {
  const {data: fetchedDevices} = useConnectedDevices()
  const allEnvironments = useMemo<EnvironmentOption[]>(() => {
    if (environments) return environments
    return [...DEFAULT_ENVIRONMENTS, ...buildLocalOptions(fetchedDevices ?? [])]
  }, [environments, fetchedDevices])

  const cloudEnvironments = useMemo(() => allEnvironments.filter(env => env.kind === 'cloud'), [allEnvironments])
  const localOptions = useMemo(() => allEnvironments.filter(env => env.kind === 'local'), [allEnvironments])

  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    () => selectedEnvironmentId ?? allEnvironments[0]?.id ?? 'background-agent',
  )
  const [isConnectDeviceDialogOpen, setIsConnectDeviceDialogOpen] = useState(false)
  const activeId = selectedEnvironmentId ?? internalSelectedId
  const selected = allEnvironments.find(env => env.id === activeId) ?? allEnvironments[0]

  // The directory dropdown lives next to the device dropdown and is only
  // meaningful when a local device is the active environment. Group local
  // options by deviceId so we can render the directories for the selected
  // device in the secondary picker.
  const localOptionsByDevice = useMemo(() => {
    const groups = new Map<string, EnvironmentOption[]>()
    for (const opt of localOptions) {
      if (!opt.deviceId) continue
      const arr = groups.get(opt.deviceId) ?? []
      arr.push(opt)
      groups.set(opt.deviceId, arr)
    }
    return groups
  }, [localOptions])

  const selectedDeviceOptions =
    selected?.kind === 'local' && selected.deviceId ? (localOptionsByDevice.get(selected.deviceId) ?? []) : []

  // Notify parent if the controlled selection has disappeared from the available
  // list (e.g. the chosen local device disconnected) so it can reset its state
  // and avoid dispatching to a now-unavailable target.
  const onSelectedEnvironmentUnavailableRef = useRef(onSelectedEnvironmentUnavailable)
  useEffect(() => {
    onSelectedEnvironmentUnavailableRef.current = onSelectedEnvironmentUnavailable
  }, [onSelectedEnvironmentUnavailable])
  const isSelectedUnavailable =
    !!selectedEnvironmentId && !allEnvironments.some(env => env.id === selectedEnvironmentId)
  useEffect(() => {
    if (isSelectedUnavailable) {
      onSelectedEnvironmentUnavailableRef.current?.()
    }
  }, [isSelectedUnavailable])

  const handleSelect = (env: EnvironmentOption) => {
    setInternalSelectedId(env.id)
    onSelectEnvironment?.(env)
  }

  // When the user picks a device from the device dropdown, auto-select its
  // first directory so the submit button is actionable without forcing a
  // second click on devices that have a single directory.
  const handleSelectDevice = (deviceId: string) => {
    const firstOption = localOptionsByDevice.get(deviceId)?.[0]
    if (firstOption) handleSelect(firstOption)
  }

  const handleConnectDevice = () => {
    if (onConnectDevice) {
      onConnectDevice()
    } else {
      setIsConnectDeviceDialogOpen(true)
    }
  }

  // The device dropdown header renders one row per *device* (not per
  // directory). The active row is determined by the device of the
  // currently-selected option, so it stays visually consistent with the
  // directory picker beside it.
  const uniqueDevices: Array<{deviceId: string; label: string; icon: EnvironmentOption['icon']}> = useMemo(() => {
    const seen = new Set<string>()
    const out: Array<{deviceId: string; label: string; icon: EnvironmentOption['icon']}> = []
    for (const opt of localOptions) {
      if (!opt.deviceId || seen.has(opt.deviceId)) continue
      seen.add(opt.deviceId)
      out.push({deviceId: opt.deviceId, label: opt.label, icon: opt.icon})
    }
    return out
  }, [localOptions])

  const TriggerIcon = selected?.icon ?? AgentIcon
  const triggerLabel = selected?.label ?? 'Background agent'
  const activeDeviceId = selected?.kind === 'local' ? selected.deviceId : undefined

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>
          {selected?.kind === 'cloud' ? (
            <IconButton
              icon={TriggerIcon}
              aria-label={`Environment: ${triggerLabel}`}
              disabled={disabled}
              variant="invisible"
              className={styles.selectorButton}
            />
          ) : (
            <Button
              leadingVisual={TriggerIcon}
              trailingAction={TriangleDownIcon}
              aria-label={`Environment: ${triggerLabel}`}
              disabled={disabled}
              variant="invisible"
              className={styles.selectorButton}
            >
              {triggerLabel}
            </Button>
          )}
        </ActionMenu.Anchor>
        {/* eslint-disable-next-line primer-react/no-system-props */}
        <ActionMenu.Overlay width="auto" overflow="auto">
          <ActionList selectionVariant="single">
            <ActionList.Group>
              <ActionList.GroupHeading>Cloud environments</ActionList.GroupHeading>
              {cloudEnvironments.map(env => (
                <ActionList.Item key={env.id} selected={env.id === activeId} onSelect={() => handleSelect(env)}>
                  <ActionList.LeadingVisual>
                    <env.icon />
                  </ActionList.LeadingVisual>
                  {env.label}
                </ActionList.Item>
              ))}
            </ActionList.Group>
            <ActionList.Group>
              <ActionList.GroupHeading>Local device</ActionList.GroupHeading>
              {uniqueDevices.length === 0 ? (
                <ActionList.Item disabled>
                  <ActionList.LeadingVisual>
                    <DeviceDesktopIcon />
                  </ActionList.LeadingVisual>
                  No connected devices
                </ActionList.Item>
              ) : (
                uniqueDevices.map(dev => (
                  <ActionList.Item
                    key={dev.deviceId}
                    selected={dev.deviceId === activeDeviceId}
                    onSelect={() => handleSelectDevice(dev.deviceId)}
                  >
                    <ActionList.LeadingVisual>
                      <dev.icon />
                    </ActionList.LeadingVisual>
                    {dev.label}
                  </ActionList.Item>
                ))
              )}
            </ActionList.Group>
            <ActionList.Group selectionVariant={false}>
              <ActionList.Item onSelect={handleConnectDevice}>
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Connect new device
              </ActionList.Item>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {selected?.kind === 'local' && (
        <DirectoryPicker
          options={selectedDeviceOptions}
          activeOptionId={activeId}
          disabled={disabled}
          onSelect={handleSelect}
        />
      )}
      {isConnectDeviceDialogOpen && <ConnectDeviceDialog onClose={() => setIsConnectDeviceDialogOpen(false)} />}
      <div className="sr-only" role="status" aria-live="polite">
        {isSelectedUnavailable
          ? 'Selected environment is no longer available. Switched to the default environment.'
          : ''}
      </div>
    </>
  )
}

// Strip trailing slashes and return the final path segment, e.g.
// "/home/mona/repo-a" -> "repo-a", "C:\\Users\\mona\\repo" -> "repo".
function basename(path: string): string {
  const trimmed = path.replace(/[/\\]+$/, '')
  const idx = Math.max(trimmed.lastIndexOf('/'), trimmed.lastIndexOf('\\'))
  return idx >= 0 ? trimmed.slice(idx + 1) || trimmed : trimmed
}

type DirectoryPickerProps = {
  options: EnvironmentOption[]
  activeOptionId: string
  disabled: boolean
  onSelect: (option: EnvironmentOption) => void
}

function DirectoryPicker({options, activeOptionId, disabled, onSelect}: DirectoryPickerProps) {
  const active = options.find(o => o.id === activeOptionId) ?? options[0]
  const fullPath = active?.workingDirectory ?? 'No directory'
  const displayLabel = active?.workingDirectory ? basename(active.workingDirectory) : fullPath
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          leadingVisual={FileDirectoryIcon}
          trailingAction={TriangleDownIcon}
          aria-label={`Directory: ${fullPath}`}
          disabled={disabled || options.length === 0}
          variant="invisible"
          className={styles.selectorButton}
        >
          {displayLabel}
        </Button>
      </ActionMenu.Anchor>
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <ActionMenu.Overlay width="auto" overflow="auto">
        <ActionList selectionVariant="single">
          {options.length === 0 ? (
            <ActionList.Item disabled>No active directories</ActionList.Item>
          ) : (
            options.map(opt => (
              <ActionList.Item key={opt.id} selected={opt.id === activeOptionId} onSelect={() => onSelect(opt)}>
                <ActionList.LeadingVisual>
                  <FileDirectoryIcon />
                </ActionList.LeadingVisual>
                {opt.workingDirectory}
              </ActionList.Item>
            ))
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
